'use client';

//
// Business Story Cards — engine.
//
// Direct port of the story-cards POC `logic.js` (normalization, progress,
// scoring, navigation, completion, analytics) into the micro module.
// Behaviour is preserved 1:1 with two deliberate edits for the LMS:
//
//   1. localStorage persistence is REMOVED. In the POC the player
//      persisted progress to localStorage; here the popup's
//      onComplete → MicroSource.progress.update is the single source
//      of truth. The renderer keeps progress in React state for the
//      duration of the open and signals completion once.
//   2. The body is typed against the story model in ./model — the
//      normalizer is the boundary that turns authored JSON into the
//      shape the player dereferences.
//
// Everything else — the deep-merge normaliser, per-card duration, the
// layer/background normalisation, scoring (cardViewed / completionBonus /
// noPauseBonus / ctaClickBonus), the event log, and the analytics
// payload — is carried over unchanged.

import { getI18n } from 'react-i18next';
import {
  type NormalizedStoryConfig,
  type StoryBackground,
  type StoryCard,
  type StoryCardMedia,
  type StoryConfig,
  type StoryLayer,
  type StoryLayerAnimation,
  type StoryProgress,
  type StoryRunStatus,
} from './model';

export type {
  NormalizedStoryConfig,
  StoryCard,
  StoryConfig,
  StoryLayer,
  StoryProgress,
} from './model';

/**
 * Lazy catalogue read for the default configuration's copy.
 *
 * <p>The defaults are built inside {@link defaultStoryCardsConfig} rather
 * than held in a module-scope constant: resolving the copy at import time
 * would freeze whichever language happened to be active at boot and never
 * follow a language switch. Reads soft-fail to an empty string when i18next
 * has not been initialised (server-side probes, isolated unit tests).
 */
function tr(key: string, values?: Record<string, unknown>): string {
  const i18n: { t(key: string, options?: Record<string, unknown>): string } | undefined = getI18n();
  return i18n ? i18n.t(key, { ns: 'site-builder-blocks', ...values }) : '';
}

/** Structural defaults every authored story is deep-merged onto. */
export function defaultStoryCardsConfig(): NormalizedStoryConfig {
  return {
    gameType: 'business_story_task',
    taskType: 'story_cards',
    version: '2.0',
    id: 'business_story_value_launch_001',
    title: tr('microStoryCards.defaults.title'),
    subtitle: tr('microStoryCards.defaults.subtitle'),
    company: { name: 'FlowLearn Labs', logoUrl: '', avatarText: 'FL' },
    story: {
      groupId: 'value_story_group',
      groupTitle: tr('microStoryCards.defaults.groupTitle'),
      coverTitle: tr('microStoryCards.defaults.groupTitle'),
      coverSubtitle: tr('microStoryCards.defaults.coverSubtitle'),
      coverImageUrl: '',
      coverImageData: '',
      seenBehavior: 'mark_complete',
      startMode: 'tap_to_start',
    },
    canvas: {
      aspectRatio: '9:16',
      width: 1080,
      height: 1920,
      safeTop: 120,
      safeBottom: 150,
      borderRadius: 34,
    },
    player: {
      autoAdvance: true,
      defaultCardDurationSeconds: 6,
      allowManualNavigation: true,
      tapToNavigate: true,
      holdToPause: true,
      pauseOnHover: true,
      showProgressBars: true,
      showHeader: true,
      showCloseButton: true,
      showMuteButton: true,
      startMuted: true,
      showCaptions: true,
      loopCards: false,
      persistProgressInLocalStorage: false,
      completionMode: 'view_all_cards',
      keyboardShortcuts: {
        next: ['ArrowRight', 'd', 'D', 'Enter'],
        previous: ['ArrowLeft', 'a', 'A'],
        pause: [' ', 'p', 'P'],
        reset: ['r', 'R'],
        mute: ['m', 'M'],
        close: ['Escape'],
      },
    },
    rules: {
      autoAdvance: true,
      defaultCardDurationSeconds: 6,
      pauseOnHover: true,
      allowManualNavigation: true,
      showProgressBars: true,
      requireViewAllCards: true,
      loopCards: false,
      persistProgressInLocalStorage: false,
      keyboardShortcuts: {
        next: ['ArrowRight', 'd', 'D', 'Enter'],
        previous: ['ArrowLeft', 'a', 'A'],
        pause: [' ', 'p', 'P'],
        reset: ['r', 'R'],
        mute: ['m', 'M'],
        close: ['Escape'],
      },
    },
    scoring: { cardViewed: 25, completionBonus: 100, noPauseBonus: 25, ctaClickBonus: 10 },
    theme: {
      name: 'storyly_business_clone',
      brandName: 'FlowLearn Labs',
      logoUrl: '',
      fontFamily:
        'Inter, ui-sans-serif, system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
      background: {
        type: 'gradient',
        value:
          'radial-gradient(circle at 18% 16%, rgba(10,102,194,.26), transparent 24rem), radial-gradient(circle at 86% 20%, rgba(34,211,154,.18), transparent 22rem), linear-gradient(135deg, #07111f, #111827 50%, #020617)',
        imageUrl: '',
        imageData: '',
        overlay: 'linear-gradient(135deg, rgba(2,6,23,.52), rgba(15,23,42,.18), rgba(2,6,23,.52))',
        size: 'cover',
        position: 'center',
        repeat: 'no-repeat',
      },
      colors: {
        primary: '#0A66C2',
        secondary: '#22D39A',
        accent: '#FFD166',
        playerChrome: '#FFFFFF',
        playerText: '#FFFFFF',
        mutedText: '#B9C6D8',
      },
      icons: { mark: '◈', story: '●', close: '×', mute: '♪' },
    },
    contentTemplates: [],
    cards: [
      {
        id: 'value-outcome',
        order: 1,
        template: 'hero_media',
        title: tr('microStoryCards.defaults.card.title'),
        durationSeconds: 6,
        description: tr('microStoryCards.defaults.card.description'),
        background: {
          type: 'color',
          color: '#0A2A4F',
          overlay:
            'linear-gradient(180deg, rgba(2,6,23,.06), rgba(2,6,23,.26) 38%, rgba(2,6,23,.92))',
        },
        layers: [
          {
            id: 'badge',
            type: 'pill',
            text: tr('microStoryCards.defaults.card.badge'),
            x: 7,
            y: 11,
            w: 42,
            h: 5,
            z: 3,
            style: {
              fontSize: 24,
              color: '#ffffff',
              background: 'rgba(10,102,194,.82)',
              align: 'center',
            },
          },
          {
            id: 'headline',
            type: 'text',
            text: tr('microStoryCards.defaults.card.headline'),
            x: 7,
            y: 58,
            w: 86,
            h: 16,
            z: 4,
            style: { fontSize: 64, weight: 1000, color: '#ffffff', align: 'left' },
          },
          {
            id: 'body',
            type: 'text',
            text: tr('microStoryCards.defaults.card.body'),
            x: 7,
            y: 76,
            w: 84,
            h: 10,
            z: 4,
            style: { fontSize: 31, weight: 750, color: 'rgba(255,255,255,.86)', align: 'left' },
          },
        ],
      },
    ],
    review: {
      showAfterCompletion: true,
      title: tr('microStoryCards.defaults.review.title'),
      description: tr('microStoryCards.defaults.review.description'),
    },
    analytics: {
      enabled: true,
      trackCardViews: true,
      trackCardTime: true,
      trackManualNavigation: true,
      trackCompletion: true,
      trackCtaClicks: true,
    },
  };
}

function clone<T>(value: T): T {
  return JSON.parse(JSON.stringify(value)) as T;
}

export function normalizeStoryCardsConfig(config?: StoryConfig): NormalizedStoryConfig {
  const base = defaultStoryCardsConfig();
  const merged = mergeDeep(clone(base), clone(config || base));
  merged.gameType = merged.gameType || 'business_story_task';
  merged.taskType = merged.taskType || 'story_cards';
  merged.version = merged.version || '2.0';
  merged.id = merged.id || 'story_cards_task';
  merged.title = merged.title || tr('microStoryCards.defaults.fallbackTitle');
  merged.subtitle = merged.subtitle || tr('microStoryCards.defaults.fallbackSubtitle');
  merged.company = mergeDeep(clone(base.company ?? {}), merged.company || {});
  merged.story = mergeDeep(clone(base.story ?? {}), merged.story || {});
  merged.canvas = mergeDeep(clone(base.canvas ?? {}), merged.canvas || {});
  merged.player = mergeDeep(clone(base.player), merged.player || merged.rules || {});
  merged.rules = mergeDeep(clone(base.rules), merged.rules || merged.player || {});
  // Keep both fields in sync for older integrations.
  merged.rules.autoAdvance = merged.player.autoAdvance;
  merged.rules.defaultCardDurationSeconds = merged.player.defaultCardDurationSeconds;
  merged.rules.pauseOnHover = merged.player.pauseOnHover;
  merged.rules.allowManualNavigation = merged.player.allowManualNavigation;
  merged.rules.showProgressBars = merged.player.showProgressBars;
  merged.rules.loopCards = merged.player.loopCards;
  merged.rules.keyboardShortcuts =
    merged.player.keyboardShortcuts || merged.rules.keyboardShortcuts || {};
  merged.scoring = mergeDeep(clone(base.scoring), merged.scoring || {});
  merged.theme = mergeDeep(clone(base.theme ?? {}), merged.theme || {});
  merged.contentTemplates = Array.isArray(merged.contentTemplates) ? merged.contentTemplates : [];
  merged.cards = normalizeCards(merged.cards, merged);
  merged.review = mergeDeep(clone(base.review ?? {}), merged.review || {});
  merged.analytics = mergeDeep(clone(base.analytics ?? {}), merged.analytics || {});
  return merged;
}

export function normalizeCards(cards: StoryCard[] = [], config?: StoryConfig): StoryCard[] {
  const base = defaultStoryCardsConfig();
  const owner = config ?? base;
  const source = Array.isArray(cards) && cards.length ? cards : (base.cards ?? []);
  return source
    .map((card: StoryCard, index: number): StoryCard => {
      const legacyMedia = card.media || {};
      const background = normalizeBackground(card.background || {}, legacyMedia, card);
      const layers = normalizeLayers(
        Array.isArray(card.layers) && card.layers.length
          ? card.layers
          : buildLegacyLayers(card, background),
        card,
        index,
      );
      return {
        id: card.id || `card_${index + 1}`,
        order: Number(card.order || index + 1),
        template: card.template || 'custom',
        title:
          card.title ||
          card.headline ||
          tr('microStoryCards.defaults.cardNumber', { number: index + 1 }),
        headline: card.headline || '',
        body: card.body || '',
        caption: card.caption || '',
        description: card.description || card.body || card.headline || '',
        durationSeconds: Math.max(
          2,
          Number(
            card.durationSeconds ||
              owner.player?.defaultCardDurationSeconds ||
              owner.rules?.defaultCardDurationSeconds ||
              6,
          ),
        ),
        background,
        layers,
        layout: card.layout || card.template || 'custom',
        backgroundColor: card.backgroundColor || background.color || '#10233D',
        textColor: card.textColor || '#F8FAFC',
        accentColor: card.accentColor || '#22D39A',
        media: {
          type:
            legacyMedia.type ||
            (background.type === 'video'
              ? 'video'
              : background.type === 'image'
                ? 'image'
                : 'none'),
          imageUrl: legacyMedia.imageUrl || background.imageUrl || '',
          imageData: legacyMedia.imageData || background.imageData || '',
          videoUrl: legacyMedia.videoUrl || background.videoUrl || '',
          videoData: legacyMedia.videoData || background.videoData || '',
          alt: legacyMedia.alt || card.title || `Story card ${index + 1}`,
          posterUrl: legacyMedia.posterUrl || background.posterUrl || '',
        },
      };
    })
    .sort((a: StoryCard, b: StoryCard) => Number(a.order || 0) - Number(b.order || 0))
    .map((card: StoryCard, index: number) => ({ ...card, order: index + 1 }));
}

export function normalizeBackground(
  background: StoryBackground = {},
  legacyMedia: StoryCardMedia = {},
  card: Partial<StoryCard> = {},
): StoryBackground {
  const type =
    background.type ||
    (legacyMedia.videoUrl || legacyMedia.videoData
      ? 'video'
      : legacyMedia.imageUrl || legacyMedia.imageData
        ? 'image'
        : background.gradient
          ? 'gradient'
          : 'color');
  return {
    type,
    color: background.color || card.backgroundColor || '#10233D',
    gradient: background.gradient || background.value || '',
    imageUrl: background.imageUrl || legacyMedia.imageUrl || '',
    imageData: background.imageData || legacyMedia.imageData || '',
    videoUrl: background.videoUrl || legacyMedia.videoUrl || '',
    videoData: background.videoData || legacyMedia.videoData || '',
    posterUrl: background.posterUrl || legacyMedia.posterUrl || '',
    overlay: background.overlay || 'linear-gradient(180deg, rgba(2,6,23,.08), rgba(2,6,23,.82))',
    fit: background.fit || background.size || 'cover',
    position: background.position || 'center',
    repeat: background.repeat || 'no-repeat',
  };
}

export function normalizeLayers(
  layers: StoryLayer[] = [],
  card: Partial<StoryCard> = {},
  cardIndex = 0,
): StoryLayer[] {
  return layers
    .map((layer: StoryLayer, index: number): StoryLayer => {
      const type = layer.type || 'text';
      const style = layer.style || {};
      return {
        id: layer.id || `layer_${cardIndex + 1}_${index + 1}`,
        type,
        text: layer.text || '',
        html: layer.html || '',
        x: clampNum(layer.x, 0, 100, 8),
        y: clampNum(layer.y, 0, 100, 12 + index * 10),
        w: clampNum(layer.w, 1, 100, 84),
        h: clampNum(layer.h, 1, 100, type === 'text' ? 12 : 10),
        z: clampNum(layer.z, 0, 99, index + 1),
        imageUrl: layer.imageUrl || '',
        imageData: layer.imageData || '',
        videoUrl: layer.videoUrl || '',
        videoData: layer.videoData || '',
        posterUrl: layer.posterUrl || '',
        fileName: layer.fileName || '',
        mimeType: layer.mimeType || '',
        source: layer.source || '',
        alt: layer.alt || card.title || '',
        action: layer.action || {},
        animation: normalizeLayerAnimation(layer.animation),
        style: {
          variant: style.variant || 'clean',
          fontFamily: style.fontFamily || '',
          fontSize: Number(style.fontSize || (type === 'text' ? 40 : 28)),
          weight: Number(style.weight || 800),
          color: style.color || card.textColor || '#FFFFFF',
          background: style.background ?? (type === 'button' ? '#FFFFFF' : 'transparent'),
          align: style.align || 'left',
          radius: Number(style.radius ?? (type === 'button' || type === 'pill' ? 999 : 26)),
          opacity: Number(style.opacity ?? 1),
          lineHeight: Number(
            style.lineHeight || (type === 'text' || type === 'quote' ? 1.08 : 1.12),
          ),
          letterSpacing: style.letterSpacing || '',
          padding: style.padding || '',
          border: style.border || '',
          borderWidth: style.borderWidth ?? '',
          borderColor: style.borderColor || '',
          shadow: style.shadow ?? true,
        },
      };
    })
    .sort((a: StoryLayer, b: StoryLayer) => Number(a.z || 0) - Number(b.z || 0));
}

function normalizeLayerAnimation(animation: StoryLayerAnimation = {}): StoryLayerAnimation {
  const allowed = new Set([
    'none',
    'fade',
    'fade-up',
    'slide-left',
    'slide-right',
    'slide-up',
    'zoom-in',
    'pop',
    'drop',
  ]);
  const rawType = animation?.type ?? 'none';
  const type = allowed.has(rawType) ? rawType : 'none';
  return {
    type,
    durationMs: clampNum(animation?.durationMs, 100, 3000, 600),
    delayMs: clampNum(animation?.delayMs, 0, 5000, 0),
    easing: animation?.easing || 'ease-out',
  };
}

function buildLegacyLayers(card: StoryCard, background: StoryBackground): StoryLayer[] {
  const layers: StoryLayer[] = [];
  if (card.eyebrow)
    layers.push({
      id: 'eyebrow',
      type: 'pill',
      text: card.eyebrow,
      x: 7,
      y: 12,
      w: 42,
      h: 5,
      z: 3,
      style: {
        fontSize: 24,
        color: '#fff',
        background: card.accentColor || 'rgba(10,102,194,.78)',
        align: 'center',
      },
    });
  if (card.headline || card.title)
    layers.push({
      id: 'headline',
      type: 'text',
      text: card.headline || card.title,
      x: 7,
      y: background.type === 'image' || background.type === 'video' ? 58 : 20,
      w: 86,
      h: 16,
      z: 4,
      style: { fontSize: 58, weight: 950, color: card.textColor || '#fff', align: 'left' },
    });
  if (card.body)
    layers.push({
      id: 'body',
      type: 'text',
      text: card.body,
      x: 7,
      y: background.type === 'image' || background.type === 'video' ? 76 : 43,
      w: 84,
      h: 12,
      z: 4,
      style: {
        fontSize: 30,
        weight: 740,
        color: card.textColor || 'rgba(255,255,255,.86)',
        align: 'left',
      },
    });
  if (card.caption)
    layers.push({
      id: 'caption',
      type: 'text',
      text: card.caption,
      x: 7,
      y: 88,
      w: 80,
      h: 5,
      z: 4,
      style: { fontSize: 22, weight: 800, color: 'rgba(255,255,255,.70)', align: 'left' },
    });
  return layers.length
    ? layers
    : [
        {
          id: 'headline',
          type: 'text',
          text: card.title || tr('microStoryCards.defaults.fallbackCardTitle'),
          x: 7,
          y: 40,
          w: 86,
          h: 16,
          z: 4,
          style: { fontSize: 54, weight: 950, color: '#fff', align: 'left' },
        },
      ];
}

export function validateStoryCardsConfig(config: StoryConfig): boolean {
  const normalized = normalizeStoryCardsConfig(config);
  if (!normalized.cards.length) throw new Error('Story Cards needs at least one card.');
  return true;
}

export function buildStoryProgress(config: StoryConfig): StoryProgress {
  const normalized = normalizeStoryCardsConfig(config);
  return {
    status: 'intro',
    currentIndex: 0,
    viewedCardIds: [],
    cardStats: {},
    score: 0,
    paused: false,
    muted: Boolean(normalized.player.startMuted),
    pausedCount: 0,
    manualNavigationCount: 0,
    ctaClicks: [],
    startedAt: null,
    completedAt: null,
    lastMessage: `Ready to view ${normalized.cards.length} story card${normalized.cards.length === 1 ? '' : 's'}.`,
    events: [],
  };
}

export function deriveStoryProgress(config: StoryConfig, progress?: StoryProgress): StoryProgress {
  const normalized = normalizeStoryCardsConfig(config);
  const base = buildStoryProgress(normalized);
  const next = { ...base, ...(progress || {}) };
  const ids = new Set(normalized.cards.map((card: StoryCard) => card.id));
  next.viewedCardIds = Array.from(
    new Set((next.viewedCardIds || []).filter((id: string) => ids.has(id))),
  );
  next.currentIndex = clampInt(next.currentIndex, 0, Math.max(0, normalized.cards.length - 1));
  next.cardStats = Object.fromEntries(
    Object.entries(next.cardStats || {}).filter(([id]) => ids.has(id)),
  );
  next.score = Number(next.score || 0);
  next.paused = Boolean(next.paused);
  next.muted = Boolean(next.muted);
  return next;
}

export function startStoryRun(config: StoryConfig, progress: StoryProgress): StoryProgress {
  const normalized = normalizeStoryCardsConfig(config);
  const current = deriveStoryProgress(normalized, progress);
  const started = logStoryEvent(
    {
      ...current,
      status: 'playing',
      startedAt: current.startedAt || Date.now(),
      completedAt: null,
      lastMessage: 'Story started.',
    },
    'story_started',
    { totalCards: normalized.cards.length },
  );
  return markStoryCardViewed(normalized, started, current.currentIndex || 0, 'start');
}

export function resetStoryRun(config: StoryConfig): StoryProgress {
  return buildStoryProgress(config);
}

export function markStoryCardViewed(
  config: StoryConfig,
  progress: StoryProgress,
  cardIndex = progress.currentIndex,
  source = 'view',
): StoryProgress {
  const normalized = normalizeStoryCardsConfig(config);
  const card = normalized.cards[cardIndex];
  if (!card) return deriveStoryProgress(normalized, progress);
  const current = deriveStoryProgress(normalized, progress);
  const alreadyViewed = current.viewedCardIds.includes(card.id);
  const viewedCardIds = alreadyViewed ? current.viewedCardIds : [...current.viewedCardIds, card.id];
  const score = current.score + (alreadyViewed ? 0 : Number(normalized.scoring.cardViewed || 0));
  const cardStats = {
    ...current.cardStats,
    [card.id]: {
      ...(current.cardStats[card.id] || {}),
      cardId: card.id,
      firstViewedAt: current.cardStats[card.id]?.firstViewedAt || Date.now(),
      lastViewedAt: Date.now(),
      viewCount: Number(current.cardStats[card.id]?.viewCount || 0) + 1,
      completed: true,
    },
  };
  return logStoryEvent({ ...current, viewedCardIds, score, cardStats }, 'card_viewed', {
    cardId: card.id,
    source,
    firstView: !alreadyViewed,
  });
}

export function goToStoryCard(
  config: StoryConfig,
  progress: StoryProgress,
  targetIndex: number,
  source = 'manual',
): StoryProgress {
  const normalized = normalizeStoryCardsConfig(config);
  const current = deriveStoryProgress(normalized, progress);
  if (current.status !== 'playing') return current;
  const clamped = clampInt(targetIndex, 0, Math.max(0, normalized.cards.length - 1));
  const next = {
    ...current,
    currentIndex: clamped,
    manualNavigationCount:
      source === 'manual'
        ? Number(current.manualNavigationCount || 0) + 1
        : Number(current.manualNavigationCount || 0),
    lastMessage: `Viewing card ${clamped + 1} of ${normalized.cards.length}.`,
  };
  return markStoryCardViewed(
    normalized,
    logStoryEvent(next, 'card_navigation', { targetIndex: clamped, source }),
    clamped,
    source,
  );
}

export function nextStoryCard(
  config: StoryConfig,
  progress: StoryProgress,
  source = 'manual',
): StoryProgress {
  const normalized = normalizeStoryCardsConfig(config);
  const current = deriveStoryProgress(normalized, progress);
  if (current.status !== 'playing') return current;
  if (current.currentIndex >= normalized.cards.length - 1) {
    if (normalized.player.loopCards) return goToStoryCard(normalized, current, 0, source);
    return finalizeStoryRun(normalized, current, 'completed');
  }
  return goToStoryCard(normalized, current, current.currentIndex + 1, source);
}

export function previousStoryCard(config: StoryConfig, progress: StoryProgress): StoryProgress {
  const normalized = normalizeStoryCardsConfig(config);
  const current = deriveStoryProgress(normalized, progress);
  if (current.status !== 'playing') return current;
  return goToStoryCard(normalized, current, Math.max(0, current.currentIndex - 1), 'manual');
}

export function toggleStoryPause(progress: StoryProgress, forceValue?: boolean): StoryProgress {
  const paused = typeof forceValue === 'boolean' ? forceValue : !progress.paused;
  return logStoryEvent(
    {
      ...progress,
      paused,
      pausedCount: paused
        ? Number(progress.pausedCount || 0) + 1
        : Number(progress.pausedCount || 0),
    },
    paused ? 'story_paused' : 'story_resumed',
    {},
  );
}

export function toggleStoryMute(progress: StoryProgress, forceValue?: boolean): StoryProgress {
  const muted = typeof forceValue === 'boolean' ? forceValue : !progress.muted;
  return logStoryEvent({ ...progress, muted }, muted ? 'story_muted' : 'story_unmuted', {});
}

export function recordStoryAction(
  config: StoryConfig,
  progress: StoryProgress,
  card: StoryCard | null | undefined,
  layer: StoryLayer | null | undefined,
): StoryProgress {
  const normalized = normalizeStoryCardsConfig(config);
  const current = deriveStoryProgress(normalized, progress);
  const action = layer?.action || {};
  const click = {
    cardId: card?.id,
    layerId: layer?.id,
    type: action.type || 'button',
    url: action.url || '',
    timestamp: new Date().toISOString(),
  };
  const next = logStoryEvent(
    {
      ...current,
      ctaClicks: [...(current.ctaClicks || []), click],
      score: current.score + Number(normalized.scoring.ctaClickBonus || 0),
    },
    'story_action_clicked',
    click,
  );
  if (action.type === 'complete') return finalizeStoryRun(normalized, next, 'completed');
  if (action.type === 'next') return nextStoryCard(normalized, next, 'cta');
  if (action.type === 'open_url' && action.url && typeof window !== 'undefined')
    window.open(action.url, '_blank', 'noopener,noreferrer');
  return next;
}

export function finalizeStoryRun(
  config: StoryConfig,
  progress: StoryProgress,
  status: StoryRunStatus = 'completed',
): StoryProgress {
  const normalized = normalizeStoryCardsConfig(config);
  const current = deriveStoryProgress(normalized, progress);
  const viewedAll = current.viewedCardIds.length >= normalized.cards.length;
  const bonusAlreadyApplied = current.status === 'completed';
  const bonus =
    !bonusAlreadyApplied && status === 'completed' && viewedAll
      ? Number(normalized.scoring.completionBonus || 0) +
        (Number(current.pausedCount || 0) === 0 ? Number(normalized.scoring.noPauseBonus || 0) : 0)
      : 0;
  const final = {
    ...current,
    status,
    paused: false,
    completedAt: current.completedAt || Date.now(),
    score: current.score + bonus,
    lastMessage: status === 'completed' ? 'Story completed.' : 'Story ended.',
  };
  return logStoryEvent(final, status === 'completed' ? 'story_completed' : 'story_ended', {
    bonus,
    viewedAll,
  });
}

export function getStoryCompletion(config: StoryConfig, progress: StoryProgress) {
  const normalized = normalizeStoryCardsConfig(config);
  const total = normalized.cards.length;
  const viewed = deriveStoryProgress(normalized, progress).viewedCardIds.length;
  const percent = total ? Math.round((viewed / total) * 100) : 0;
  return { total, viewed, remaining: Math.max(0, total - viewed), percent };
}

export function getCurrentStoryCard(
  config: StoryConfig,
  progress: StoryProgress,
): StoryCard | null {
  const normalized = normalizeStoryCardsConfig(config);
  return (
    normalized.cards[
      clampInt(progress.currentIndex || 0, 0, Math.max(0, normalized.cards.length - 1))
    ] ||
    normalized.cards[0] ||
    null
  );
}

export function getCardDurationMs(config: StoryConfig, card: StoryCard | null | undefined): number {
  const normalized = normalizeStoryCardsConfig(config);
  return Math.max(
    1600,
    Number(card?.durationSeconds || normalized.player.defaultCardDurationSeconds || 6) * 1000,
  );
}

export function getCardBackgroundSource(card: StoryCard | null | undefined): string {
  const bg = card?.background || {};
  if (bg.type === 'video') return bg.videoData || bg.videoUrl || bg.imageData || bg.imageUrl || '';
  if (bg.type === 'image') return bg.imageData || bg.imageUrl || '';
  return '';
}

export function getLayerMediaSource(layer: StoryLayer | null | undefined): string {
  if (!layer) return '';
  if (layer.type === 'video') return layer.videoData || layer.videoUrl || '';
  if (layer.type === 'image') return layer.imageData || layer.imageUrl || '';
  return layer.imageData || layer.imageUrl || layer.videoData || layer.videoUrl || '';
}

export function buildStoryAnalytics(config: StoryConfig, progress: StoryProgress) {
  const normalized = normalizeStoryCardsConfig(config);
  const current = deriveStoryProgress(normalized, progress);
  const durationSeconds = current.startedAt
    ? Math.max(0, Math.round(((current.completedAt || Date.now()) - current.startedAt) / 1000))
    : 0;
  return {
    taskId: normalized.id,
    gameType: normalized.gameType,
    taskType: normalized.taskType,
    version: normalized.version,
    courseId: normalized.course?.courseId || '',
    moduleId: normalized.course?.moduleId || '',
    storyGroupId: normalized.story?.groupId || '',
    result: {
      status: current.status,
      score: current.score,
      cardsViewed: current.viewedCardIds.length,
      totalCards: normalized.cards.length,
      completionPercent: getStoryCompletion(normalized, current).percent,
      durationSeconds,
      manualNavigationCount: Number(current.manualNavigationCount || 0),
      ctaClicks: (current.ctaClicks || []).length,
      completed: current.status === 'completed',
    },
    cards: normalized.cards.map((card: StoryCard, index: number) => ({
      cardId: card.id,
      index,
      title: card.title,
      template: card.template,
      viewed: current.viewedCardIds.includes(card.id),
      viewCount: Number(current.cardStats?.[card.id]?.viewCount || 0),
      description: card.description || '',
    })),
    actions: current.ctaClicks || [],
    events: current.events || [],
  };
}

/**
 * The compact result persisted to MicroProgress.result on completion.
 * Matches the Phase 3 contract: { viewedCardIds, cardsViewed, total,
 * completed, score, ctaClicks, completedAt }. `score` drives the
 * per-item leaderboard (Phase 4).
 */
export function buildStoryResult(config: StoryConfig, progress: StoryProgress) {
  const normalized = normalizeStoryCardsConfig(config);
  const current = deriveStoryProgress(normalized, progress);
  const completion = getStoryCompletion(normalized, current);
  return {
    viewedCardIds: current.viewedCardIds,
    cardsViewed: completion.viewed,
    total: completion.total,
    completionPercent: completion.percent,
    completed: current.status === 'completed',
    score: Number(current.score || 0),
    ctaClicks: (current.ctaClicks || []).length,
    completedAt: current.completedAt || Date.now(),
  };
}

function logStoryEvent(
  progress: StoryProgress,
  type: string,
  data: Record<string, unknown> = {},
): StoryProgress {
  const event = { type, timestamp: new Date().toISOString(), ...data };
  return { ...progress, events: [...(progress.events || []), event].slice(-250) };
}

function clampInt(value: unknown, min: number, max: number): number {
  return Math.max(
    min,
    Math.min(max, Number.isFinite(Number(value)) ? Math.round(Number(value)) : min),
  );
}

function clampNum(value: unknown, min: number, max: number, fallback: number): number {
  const number = Number(value);
  return Math.max(min, Math.min(max, Number.isFinite(number) ? number : fallback));
}

/** Recursive JSON merge: arrays replace, objects merge, scalars
 *  overwrite. Mutates and returns {@code target} — the one genuinely
 *  dynamic boundary in the engine, hence the untyped walk. */
function mergeDeep<T extends object>(target: T, source: unknown): T {
  if (!source || typeof source !== 'object') return target;
  const out = target as Record<string, unknown>;
  for (const [key, value] of Object.entries(source)) {
    if (Array.isArray(value)) out[key] = value;
    else if (value && typeof value === 'object') {
      const existing = out[key];
      out[key] = mergeDeep(
        existing && typeof existing === 'object' && !Array.isArray(existing)
          ? (existing as Record<string, unknown>)
          : {},
        value,
      );
    } else out[key] = value;
  }
  return target;
}
